import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack';

import { Order } from '@luxuryescapes/contract-svc-order';
import { formatDate } from '@luxuryescapes/lib-refunds';
import { getCurrencyCodes } from '@luxuryescapes/lib-regions';

import { LE_ENTITY_AUS, LE_ENTITY_INTL } from '~/components/Common/CustomOffers/constants';

import { getDailyFxRates } from '~/services/ReportingService';
import { getFxRates } from '~/services/fxService';

import { getCurrencyCodeByRegionCode } from './getCurrencyCodeByRegionCode';

export const finePrintToString = (finePrint: FinePrint): string => {
  return `#### Check-in
  Check-in starts at ${finePrint.checkIn?.beginTime}.
  Check-in ends at ${finePrint.checkIn?.endTime}.
  
  #### Check-out
  Check-out before at ${finePrint.checkOut?.time}
  
  #### Special Instructions
  
  ${finePrint.checkIn?.instructions}
  
  #### Special check-in instructions
  
  ${finePrint.checkIn?.specialInstructions}
  
  ${finePrint.pets ? '#### Pets' : ''}
  ${finePrint.pets.join('\n')}
  
  #### Fees
  ${finePrint.fees?.mandatory ? '##### Mandatory' : ''}
  ${finePrint.fees?.mandatory ? finePrint.fees?.mandatory : ''}
  ${finePrint.fees?.optional ? '##### Optional' : ''}
  ${finePrint.fees?.optional ? finePrint.fees?.optional : ''}
  
  #### Know Before You Go
  ${finePrint.policies?.knowBeforeYouGo}
  `;
};

export const combinePolicies = (cancellation: string, finePrint: string): string => {
  return `### Cancellation Policies
${cancellation}
### Fine Print
${finePrint}
`;
};

export const combineDescription = (
  roomType: string,
  description: string,
  specialRequests = '',
  facilities = [],
  promotionInclusions = '',
): string => {
  const hasInclusions = facilities.length > 0 || promotionInclusions;
  return `### Room Type
${roomType}
### Description
${description}
${hasInclusions && `### Inclusions`}
${promotionInclusions ? `${promotionInclusions}\n` : ''}
${facilities.length > 0 ? `- ${facilities.join('\n- ')}` : ''}
${specialRequests !== '' ? `### Special Requests\n${specialRequests}` : ''}
`;
};

export const bedbankCancellationToString = (room: Order.BedbankItemRoom, propertyTimezone: string): string => {
  const cancellationPolicy = room.cancellation_policies[0];
  if (room.refundable) {
    return `Free cancellation until ${formatDate.formattedDateByTimezone(
      cancellationPolicy.start,
      propertyTimezone,
    )} (local time of property)`;
  } else if (room.partially_refundable) {
    return `Partially Refundable`;
  } else {
    return `Non-refundable`;
  }
};

export const lpcCancellationToString = (cancellationPolicy: App.CancellationPolicy): string => {
  if (cancellationPolicy.cancellation_policy_details) {
    return `${cancellationPolicy.cancellation_policy_details.join('\n')}`;
  }
  return `${cancellationPolicy.cancellation_policy}`;
};

export const setUpTravellers = (handleAddNewTraveller, reservation) => {
  ['adult', 'child', 'infant'].forEach((type) => {
    const count = reservation[`number_of_${type === 'child' ? 'children' : `${type}s`}`];
    // minus 1 due to the default adult being there
    for (let i = 0; i < (type === 'adult' ? count - 1 : count); i++) {
      handleAddNewTraveller(type);
    }
  });
};

export const getBedbankCancellationDate = (room: Order.BedbankItemRoom, propertyTimezone: string) => {
  const cancellationPolicy = room.cancellation_policies[0];
  if (cancellationPolicy?.start) {
    try {
      const dateForConverting = new Date(cancellationPolicy.start);
      const convertedDate = formatDate.convertDateByTimezone(dateForConverting, propertyTimezone);
      return `${convertedDate.getFullYear()}-${convertedDate.getMonth() + 1}-${convertedDate.getDate()}`;
    } catch (err) {
      throw new Error(`Could not parse date: ${cancellationPolicy.start} - ${err.message}`, { cause: err });
    }
  }
  return null;
};

export const deriveCustomerCurrency = (brand: string, userCountryCode: string): string | null => {
  const derivedCurrency = getCurrencyCodeByRegionCode(userCountryCode);
  if (derivedCurrency && getCurrencyCodes(brand).includes(derivedCurrency)) {
    return derivedCurrency;
  }
  return null;
};

export const deriveLeEntity = (currency: string) => {
  return currency === 'AUD' ? LE_ENTITY_AUS : LE_ENTITY_INTL;
};

/**
 * Convert vendor-currency cost + margin (+ optional fee) into customer currency.
 * Matches CustomOfferForm: (cost + margin + fee) * (customerRate / vendorRate).
 */
export const calculateCustomerSellPriceWithFx = ({
  costPrice,
  margin = 0,
  serviceFee = 0,
  customerExchangeRate,
  vendorExchangeRate,
}: {
  costPrice: number;
  margin?: number;
  serviceFee?: number;
  customerExchangeRate: number;
  vendorExchangeRate: number;
}): number => {
  const priceTotalInCostCurrency = Number(costPrice) + Number(margin) + Number(serviceFee);
  const customerTotal = priceTotalInCostCurrency * (Number(customerExchangeRate) / Number(vendorExchangeRate));
  return Math.round((customerTotal + Number.EPSILON) * 100) / 100;
};

export interface CurrencyDict {
  [key: string]: number;
}

export const fetchSpotAndContractedRates = async (
  currencies: Array<string>,
  enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey,
) => {
  const [dailyRatesResponse, contractedRatesResponse] = await Promise.all([
    getDailyFxRates({ currency_from: 'AUD' }),
    getFxRates(),
  ]);

  const customerFxRates: CurrencyDict = {};
  const contractFxRates: CurrencyDict = {};

  const findRate = (currency: string) => {
    const dailyRate = dailyRatesResponse.find((fxRate) => fxRate.currencyTo === currency);
    const contractedRate = contractedRatesResponse.find((fxRate) => fxRate.currency === currency);
    return { dailyRate, contractedRate };
  };

  // Customer is restricted to the currencies supported by the brand (LR, Scoopon)
  currencies.forEach((currency) => {
    const { dailyRate, contractedRate } = findRate(currency);

    if (dailyRate || contractedRate) {
      // For customer, we always want the highest rate as vendor is fixed
      customerFxRates[currency] = Math.max(dailyRate?.rate, contractedRate?.rate);
    } else {
      enqueueSnackbar(`Exchange rate not found for ${currency}`, { variant: 'error' });
    }
  });

  // Vendor is not restricted to the currencies in the request
  // All contracted currencies are available for the vendor.
  const contractedCurrencies = contractedRatesResponse.map((fxRate) => fxRate.currency);
  contractedCurrencies.forEach((currency) => {
    const { dailyRate, contractedRate } = findRate(currency);

    if (dailyRate || contractedRate) {
      // if a rate for a currency is missing, fall back to an daily fx rate value
      // if they are all missing for a currency, show an error
      contractFxRates[currency] = contractedRate?.rate ?? dailyRate?.rate;
    } else {
      enqueueSnackbar(`Exchange rate not found for ${currency}`, { variant: 'error' });
    }
  });

  return { customerFxRates, contractFxRates };
};
